import { useState, useCallback } from 'preact/hooks'
import { RotateCcw } from 'lucide-preact'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { assetApi } from '@/api/asset'
import { useToast } from '@/hooks/useToast'
import { getDisplayErrorMessages } from '@/utils/errorHandling'

interface ResetCopyToTemplateButtonProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    assetId: string
    /** Disable when there's no bound template (nothing to reset to) or while the editor
     *  is non-editable (e.g. Webster is regenerating). */
    disabled?: boolean
    /** Fires after a successful reset so the parent can refetch the asset — the editor
     *  panel reseeds its UnlayerDesigner off the new DraftDesign in response. */
    onReset?: () => Promise<void> | void
}

/**
 * "Reset to template" action surfaced in the Email Copy editor. Re-clones the currently
 * bound template's DraftDesign onto the copy, discarding the copy's customizations.
 * Behind a confirmation modal so accidental clicks don't blow away user edits.
 */
export function ResetCopyToTemplateButton({
    clientCompanyId,
    workspaceId,
    campaignId,
    assetId,
    disabled,
    onReset,
}: ResetCopyToTemplateButtonProps) {
    const [showConfirm, setShowConfirm] = useState(false)
    const [resetting, setResetting] = useState(false)
    const { showError } = useToast()

    const handleConfirm = useCallback(async () => {
        setResetting(true)
        const response = await assetApi.resetCopyToTemplate(clientCompanyId, workspaceId, campaignId, assetId)
        setResetting(false)
        if (response.data) {
            setShowConfirm(false)
            await onReset?.()
        } else {
            showError('Reset failed', getDisplayErrorMessages(response.error, 'Could not reset the copy to its template.').join(' '))
        }
    }, [clientCompanyId, workspaceId, campaignId, assetId, onReset, showError])

    return (
        <>
            <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={disabled || resetting}
                title="Reset this email's design back to the bound template"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset to template
            </button>

            <ConfirmationModal
                isOpen={showConfirm}
                title="Reset to template?"
                message="Your custom edits will be discarded and the design will be reset to the current template. This action cannot be undone."
                type="warning"
                confirmText="Reset"
                cancelText="Cancel"
                loading={resetting}
                onConfirm={handleConfirm}
                onCancel={() => { if (!resetting) setShowConfirm(false) }}
            />
        </>
    )
}
